"use client";

import * as React from "react";
import { ChevronsUpDown, Plus, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/components/ui/sidebar";
import buzookaIcon from "@/assets/icons/buzooka-icon.svg";
import { ProjectContext, ProjectType } from "@/context/project";

export function ProjectSwitcher({ projects, hideLogo = false }: { projects: ProjectType[], hideLogo?: boolean }) {
  const { isMobile } = useSidebar();
  const { activeProject, setActiveProject } = React.useContext(ProjectContext);
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="flex items-center gap-1 cursor-pointer group">
          {!hideLogo && (
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg text-sidebar-primary-foreground">
              <img
                src={buzookaIcon}
                alt="Buzooka"
                height={32}
                className="max-h-8"
              />
            </div>
          )}
          <div className="grid flex-1 text-left text-sm">
            {!activeProject ? (
              <span className="truncate text-xs">
                {"Select Project"}
              </span>
            ) : 
            <span className="truncate font-medium">
              {activeProject?.name}
            </span>}
          </div>
          <ChevronsUpDown size={16} className={`ml-auto transition-opacity ${activeProject ? "opacity-20 group-hover:opacity-100" : ""}`} />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
        align="start"
        side={isMobile ? "bottom" : "bottom"}
        sideOffset={4}
        collisionPadding={8}
      >
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Projects
        </DropdownMenuLabel>
        {projects?.length &&
          projects.map((project, index) => (
            <DropdownMenuItem
              key={project.name}
              onClick={() => {
                setActiveProject(project);
                navigate(`/projects/${project.id}`);
              }}
              className={`cursor-pointer gap-2 p-2 ${
                project.name === activeProject?.name
                  ? ""
                  : "pl-8 hover:opacity-60"
              }`}
            >
              {project.name === activeProject?.name && <Check />}
              {project.name}
              <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
            </DropdownMenuItem>
          ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer gap-2 p-2"
          onClick={() => {
            setActiveProject(null);
            navigate("/new");
          }}
        >
          <div className="flex items-center gap-2">
            <div className="flex size-6 items-center justify-center rounded-md border bg-background">
              <Plus className="size-4" />
            </div>
            <div className="font-medium text-muted-foreground">
              New Project
            </div>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
